import { DataRefreshEvent, type BatchProcessDto } from "@fp/admin-api-client";
import {
  getListBatchProcessesQueryKey,
  useListBatchProcesses,
} from "@fp/admin-api-client/batch-processes/batch-processes.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserContext } from "@/components/user-context";
import { useDataRefresh } from "@/hooks/useSignalR";
import {
  createBatchProcessRefresh,
  getBatchProcessReconcileInterval,
} from "./batch-process-refresh";

export type BatchProcessClaim = (batch: BatchProcessDto) => boolean;

interface BatchProcessContextValue {
  batches: BatchProcessDto[];
  canView: boolean;
  pendingBatch: BatchProcessDto | null;
  announcePendingBatch: (pending: BatchProcessDto) => void;
  clearPendingBatch: () => void;
  registerClaim: (claim: BatchProcessClaim) => () => void;
  isClaimedByCurrentView: BatchProcessClaim;
}

const BatchProcessContext = createContext<BatchProcessContextValue | null>(null);

export function BatchProcessProvider({ children }: { children: ReactNode }) {
  const { orgId } = useUserContext();
  const queryClient = useQueryClient();

  const { data: capabilitiesResponse } =
    useGetOrganizationContextCapabilities();
  const canView =
    capabilitiesResponse?.data?.canViewBatchProcesses ?? false;

  const { data: batchesResponse } = useListBatchProcesses({
    query: {
      enabled: canView,
      queryKey: [...getListBatchProcessesQueryKey(), { orgId }],
      refetchInterval: (query) =>
        getBatchProcessReconcileInterval(query.state.data?.data),
    },
  });

  const batches = useMemo(
    () => batchesResponse?.data ?? [],
    [batchesResponse],
  );

  const refreshBatchProcesses = useMemo(
    () => createBatchProcessRefresh(queryClient),
    [queryClient],
  );

  const [pendingBatch, setPendingBatch] = useState<BatchProcessDto | null>(null);

  const announcePendingBatch = useCallback((pending: BatchProcessDto) => {
    setPendingBatch(pending);
  }, []);

  const clearPendingBatch = useCallback(() => {
    setPendingBatch(null);
  }, []);

  const claims = useRef<Set<BatchProcessClaim>>(new Set());

  const registerClaim = useCallback((claim: BatchProcessClaim) => {
    claims.current.add(claim);

    return () => {
      claims.current.delete(claim);
    };
  }, []);

  const isClaimedByCurrentView = useCallback(
    (batch: BatchProcessDto) =>
      [...claims.current].some((claim) => claim(batch)),
    [],
  );

  useDataRefresh(DataRefreshEvent.BatchProcessCreated, () => {
    refreshBatchProcesses();
    clearPendingBatch();
  });
  useDataRefresh(DataRefreshEvent.BatchProcessItemRequeued, refreshBatchProcesses);
  useDataRefresh(DataRefreshEvent.BatchProcessItemCompleted, refreshBatchProcesses);
  useDataRefresh(DataRefreshEvent.BatchProcessCompleted, refreshBatchProcesses);

  const value = useMemo<BatchProcessContextValue>(
    () => ({
      batches,
      canView,
      pendingBatch,
      announcePendingBatch,
      clearPendingBatch,
      registerClaim,
      isClaimedByCurrentView,
    }),
    [
      batches,
      canView,
      pendingBatch,
      announcePendingBatch,
      clearPendingBatch,
      registerClaim,
      isClaimedByCurrentView,
    ],
  );

  return (
    <BatchProcessContext.Provider value={value}>
      {children}
    </BatchProcessContext.Provider>
  );
}

export function useBatchProcesses(): BatchProcessContextValue {
  const context = useContext(BatchProcessContext);
  if (!context) {
    throw new Error(
      "useBatchProcesses must be used within a BatchProcessProvider",
    );
  }
  return context;
}

export function useBatchProcessClaim(claim: BatchProcessClaim) {
  const { registerClaim } = useBatchProcesses();
  const latestClaim = useRef(claim);
  latestClaim.current = claim;

  useEffect(
    () => registerClaim((batch) => latestClaim.current(batch)),
    [registerClaim],
  );
}
